/**
 * OS notifications stay in the service worker when this device is subscribed
 * and the tab is in the background. A focused window always gets the banner:
 * via the service worker if push is on, otherwise by polling the inbox.
 */

import { noticeSkin } from "@shared/push-shared.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { type AppConfig, api, type Notice } from "../../lib/api.ts";
import { EDM_PUSH_EVENT } from "../../lib/notifications.ts";
import { thisDeviceCanPush } from "../../lib/push-client.ts";
import { OsNotice } from "./OsNotice.tsx";
import { PushOptIn } from "./PushOptIn.tsx";

const POLL_MS = 15_000;
const TOAST_MS = 8_000;

type PushMessage = {
  type?: string;
  title?: string;
  body?: string;
  url?: string;
  event?: string | null;
};

function pathFromHref(href: string) {
  try {
    const parsed = new URL(href, window.location.origin);
    if (parsed.origin !== window.location.origin) return "/";
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || "/";
  } catch {
    return "/";
  }
}

export function NotificationsHost() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<AppConfig>("/config"),
    staleTime: 5 * 60_000,
  });
  const user = data?.user ?? null;
  const [canPush, setCanPush] = useState<boolean | null>(null);

  const showToast = useCallback(
    (title: string, body: string, url: string) => {
      const href = pathFromHref(url);
      const skin = noticeSkin(navigator.userAgent);
      toast.custom(
        (id) => (
          <button
            type="button"
            className="w-full text-left"
            onClick={() => {
              toast.dismiss(id);
              void navigate({ href });
            }}
          >
            <OsNotice title={title} body={body || undefined} skin={skin} />
          </button>
        ),
        { duration: TOAST_MS, unstyled: true },
      );
    },
    [navigate],
  );

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    if (import.meta.env.PROD) {
      if (!user) return;
      void navigator.serviceWorker.register("/sw.js");
    } else {
      void navigator.serviceWorker.getRegistrations().then(async (regs) => {
        for (const reg of regs) {
          if (await reg.pushManager.getSubscription()) return;
        }
        await Promise.all(regs.map((reg) => reg.unregister()));
      });
    }

    function onMessage(event: MessageEvent<PushMessage>) {
      const message = event.data;
      if (message?.type !== "edm-push") return;
      window.dispatchEvent(new Event(EDM_PUSH_EVENT));
      if (!document.hasFocus()) return;
      const title =
        typeof message.title === "string" && message.title.trim()
          ? message.title.trim()
          : "eConnect DM";
      showToast(
        title,
        typeof message.body === "string" ? message.body : "",
        String(message.url || "/"),
      );
    }
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [showToast, user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void thisDeviceCanPush().then((ready) => {
      if (!cancelled) setCanPush(ready);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || canPush !== false) return;
    let cancelled = false;
    let since = new Date().toISOString();
    let timer: number | undefined;

    async function tick() {
      if (cancelled || document.visibilityState !== "visible") return;
      try {
        const json = await api.get<{
          notices?: Pick<Notice, "id" | "title" | "body" | "url" | "createdAt">[];
        }>(`/notifications/unread?since=${encodeURIComponent(since)}`);
        const notices = json.notices ?? [];
        if (notices.length === 0) return;
        for (const notice of notices) {
          showToast(notice.title.trim() || "eConnect DM", notice.body, notice.url);
        }
        window.dispatchEvent(new Event(EDM_PUSH_EVENT));
        since = notices[notices.length - 1]?.createdAt ?? since;
        void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      } catch {
        // ponytail: next tick retries
      }
    }

    void tick();
    timer = window.setInterval(() => void tick(), POLL_MS);
    function onVisible() {
      if (document.visibilityState === "visible") void tick();
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [canPush, queryClient, showToast, user]);

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          unstyled: true,
          classNames: { toast: "w-[min(24rem,calc(100vw-1.5rem))]" },
        }}
      />
      <PushOptIn signedIn={Boolean(user)} />
    </>
  );
}
